import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import * as api from '../services/ClusteringLabService';
import { ClusteringLabShareworthyPhoto } from '../types';

const ZOOM_DELAY_MS = 3000;
const ZOOM_SIZE_PX = 480;

interface ShareworthyPanelProps {
  userLid: number;
}

const Container = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: #f7fafc;
`;

const Stats = styled.div`
  max-width: 900px;
  margin: 0 auto 16px;
  font-size: 13px;
  color: #718096;
  text-align: center;
`;

const Grid = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const Card = styled.div<{ $shareworthy: boolean }>`
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  border: 3px solid ${(p) => (p.$shareworthy ? '#48bb78' : '#fc8181')};
`;

const ImgWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  cursor: pointer;
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Badge = styled.div<{ $shareworthy: boolean }>`
  position: absolute;
  top: 6px;
  left: 6px;
  background: ${(p) => (p.$shareworthy ? 'rgba(72,187,120,0.9)' : 'rgba(252,129,129,0.9)')};
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  pointer-events: none;
`;

const ConfBadge = styled.div`
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  pointer-events: none;
`;

const Actions = styled.div`
  display: flex;
  gap: 0;
`;

const ActionBtn = styled.button<{ $variant: 'yes' | 'no' }>`
  flex: 1;
  padding: 8px 0;
  border: none;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.1s;
  background: ${(p) => (p.$variant === 'yes' ? '#f0fff4' : '#fff5f5')};
  color: ${(p) => (p.$variant === 'yes' ? '#276749' : '#9b2c2c')};

  &:hover {
    background: ${(p) => (p.$variant === 'yes' ? '#c6f6d5' : '#fed7d7')};
  }
`;

const ZoomPreview = styled.div<{ $left: number; $top: number }>`
  position: fixed;
  left: ${(p) => p.$left}px;
  top: ${(p) => p.$top}px;
  z-index: 1000;
  width: ${ZOOM_SIZE_PX}px;
  height: ${ZOOM_SIZE_PX}px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  pointer-events: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #111;
  }
`;

const Empty = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #a0aec0;
  font-size: 14px;
`;

const LoadMore = styled.button`
  display: block;
  margin: 16px auto;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 24px;
  font-size: 13px;
  color: #4a5568;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #f7fafc;
  }
`;

interface ZoomState {
  url: string;
  left: number;
  top: number;
}

const ShareworthyPanel: React.FC<ShareworthyPanelProps> = ({ userLid }) => {
  const [photos, setPhotos] = useState<ClusteringLabShareworthyPhoto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [confirmed, setConfirmed] = useState(0);
  const [zoom, setZoom] = useState<ZoomState | null>(null);
  const zoomTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleHoverStart = useCallback((url: string, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (zoomTimerRef.current) clearTimeout(zoomTimerRef.current);
    zoomTimerRef.current = setTimeout(() => {
      const margin = 12;
      const wantRight = rect.right + margin;
      const fitsRight = wantRight + ZOOM_SIZE_PX <= window.innerWidth;
      const left = fitsRight ? wantRight : Math.max(margin, rect.left - ZOOM_SIZE_PX - margin);
      const top = Math.max(margin, Math.min(window.innerHeight - ZOOM_SIZE_PX - margin, rect.top));
      setZoom({ url, left, top });
    }, ZOOM_DELAY_MS);
  }, []);

  const handleHoverEnd = useCallback(() => {
    if (zoomTimerRef.current) {
      clearTimeout(zoomTimerRef.current);
      zoomTimerRef.current = null;
    }
    setZoom(null);
  }, []);

  useEffect(() => {
    return () => {
      if (zoomTimerRef.current) clearTimeout(zoomTimerRef.current);
    };
  }, []);

  const load = useCallback(
    async (nextCursor: string | null) => {
      setLoading(true);
      try {
        const res = await api.listShareworthyPhotos(userLid, nextCursor);
        if (!res) return;
        setPhotos((prev) => (nextCursor ? [...prev, ...res.photos] : res.photos));
        setCursor(res.next_cursor ?? null);
        setHasMore(!!res.next_cursor);
        setTotal(res.total ?? 0);
        setConfirmed(res.confirmed ?? 0);
      } finally {
        setLoading(false);
      }
    },
    [userLid]
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  const handleConfirm = useCallback(
    async (photoId: string, isShareworthy: boolean) => {
      await api.confirmShareworthy(userLid, [photoId], isShareworthy);
      setPhotos((prev) => prev.filter((p) => p.photo_id !== photoId));
      setConfirmed((c) => c + 1);
    },
    [userLid]
  );

  if (loading && photos.length === 0) {
    return (
      <Container>
        <Empty>Loading photos...</Empty>
      </Container>
    );
  }

  if (photos.length === 0) {
    return (
      <Container>
        <Empty>
          No photos to review.
          {total > 0 && ` (${confirmed} of ${total} confirmed)`}
        </Empty>
      </Container>
    );
  }

  return (
    <Container>
      <Stats>
        {photos.length} to review &middot; {confirmed} of {total} confirmed
      </Stats>
      <Grid>
        {photos.map((p) => (
          <Card key={p.photo_id} $shareworthy={p.is_shareworthy}>
            <ImgWrap onMouseEnter={(e) => handleHoverStart(p.photo_url, e)} onMouseLeave={handleHoverEnd}>
              <Img src={p.photo_url} loading="lazy" />
              <Badge $shareworthy={p.is_shareworthy}>{p.is_shareworthy ? 'Shareworthy' : 'Not Shareworthy'}</Badge>
              {p.confidence != null && <ConfBadge>{p.confidence.toFixed(2)}</ConfBadge>}
            </ImgWrap>
            <Actions>
              <ActionBtn $variant="yes" onClick={() => handleConfirm(p.photo_id, true)}>
                Shareworthy
              </ActionBtn>
              <ActionBtn $variant="no" onClick={() => handleConfirm(p.photo_id, false)}>
                Not Shareworthy
              </ActionBtn>
            </Actions>
          </Card>
        ))}
      </Grid>
      {hasMore && (
        <LoadMore onClick={() => load(cursor)} disabled={loading}>
          {loading ? 'Loading...' : 'Load More'}
        </LoadMore>
      )}
      {zoom && (
        <ZoomPreview $left={zoom.left} $top={zoom.top}>
          <img src={zoom.url} alt="" />
        </ZoomPreview>
      )}
    </Container>
  );
};

export default ShareworthyPanel;
